import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * Custom hook for implementing lazy loading with intersection observer
 * 
 * @param {Array} items - Array of items to lazy load
 * @param {Object} options - Configuration options
 * @param {number} options.initialLoad - Number of items to load initially (default: 5)
 * @param {number} options.itemsPerLoad - Number of items to load per batch (default: 5)
 * @param {number} options.threshold - Intersection observer threshold (default: 0.1)
 * @param {number} options.loadDelay - Delay in ms before loading more items (default: 300)
 * @param {Function} options.sortFn - Optional sort function for items
 * @param {any} options.resetDependency - Dependency that triggers reset of visible items
 * 
 * @returns {Object} - Lazy loading state and utilities
 */
const useLazyLoad = (items = [], options = {}) => {
  const {
    initialLoad = 5,
    itemsPerLoad = 5,
    threshold = 0.1,
    loadDelay = 1000,
    sortFn = null,
    resetDependency = null,
  } = options;

  const [visibleCount, setVisibleCount] = useState(initialLoad);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef(null);
  const observerRef = useRef(null);
  const shouldObserveRef = useRef(true);

  // Sort items if sort function is provided
  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    if (sortFn && typeof sortFn === 'function') {
      return [...items].sort(sortFn);
    }
    return items;
  }, [items, sortFn]);

  // Get displayed items
  const displayedItems = useMemo(() => {
    return sortedItems.slice(0, visibleCount);
  }, [sortedItems, visibleCount]);

  // Check if there are more items to load
  const hasMore = visibleCount < sortedItems.length;

  // Load more items function
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || !shouldObserveRef.current) return;
    
    // Temporarily disable observer to prevent immediate re-trigger
    shouldObserveRef.current = false;
    setIsLoadingMore(true);
    
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + itemsPerLoad, sortedItems.length));
      setIsLoadingMore(false);
      
      // Re-enable observer after a delay to allow content to settle
      setTimeout(() => {
        shouldObserveRef.current = true;
      }, 100);
    }, loadDelay);
  }, [isLoadingMore, hasMore, itemsPerLoad, sortedItems.length, loadDelay]);

  // Intersection observer for automatic loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && shouldObserveRef.current) {
          loadMore();
        }
      },
      { 
        threshold,
        // Trigger loading 200px before reaching the loader element
        rootMargin: '0px 0px 200px 0px'
      }
    );

    observerRef.current = observer;
    const currentLoader = loaderRef.current;
    
    if (currentLoader && hasMore) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, loadMore, threshold]);

  // Reset visible count when reset dependency changes
  useEffect(() => {
    if (resetDependency !== null) {
      setVisibleCount(initialLoad);
      shouldObserveRef.current = true;
    }
  }, [resetDependency, initialLoad]);

  // Reset when items array changes significantly
  useEffect(() => {
    if (sortedItems.length < visibleCount) {
      setVisibleCount(Math.min(initialLoad, sortedItems.length));
    }
  }, [sortedItems.length, visibleCount, initialLoad]);

  return {
    // Data
    displayedItems,
    allItems: sortedItems,
    
    // State
    visibleCount,
    isLoadingMore,
    hasMore,
    
    // Utilities
    loadMore,
    loaderRef,
    
    // Computed values
    remainingCount: sortedItems.length - visibleCount,
    totalCount: sortedItems.length,
    itemsPerLoad,
  };
};

export default useLazyLoad;
